import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { boxService } from '@/services/boxService';
import { Box, Device, Attribute } from '@/types';

interface BoxDetailsSidebarProps {
  boxId: number | null;
  isOpen: boolean;
  onClose: () => void;
  currentProjectId: number;
}

export function BoxDetailsSidebar({ boxId, isOpen, onClose, currentProjectId }: BoxDetailsSidebarProps) {
  const { data: box, isLoading, error } = useQuery({
    queryKey: ['box-details', boxId],
    queryFn: () => {
      if (!boxId) throw new Error('Box ID is required');
      return boxService.getBoxWithDevicesAndAttributes(boxId);
    },
    enabled: isOpen && !!boxId,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (box && box.project?.id && box.project.id !== currentProjectId) {
      onClose();
    }
  }, [box, currentProjectId, onClose]);

  const getAttributeOnlineStatus = (status: unknown): boolean => {
    if (status === true || status === 1 || status === '1') return true;
    if (typeof status === 'string' && status.toLowerCase() === 'true') return true;
    return false;
  };

  const devicesWithSortedAttributes = useMemo(() => {
    const devices = box?.devices;
    if (!Array.isArray(devices)) {
      return [];
    }

    return devices.map((device: Device) => {
      const rawAttributes = (device as Device).attributes;
      const attributesArray: Attribute[] = Array.isArray(rawAttributes)
        ? rawAttributes
        : rawAttributes
          ? (Object.values(rawAttributes as Record<string, unknown>) as Attribute[])
          : [];

      const sortedAttributes = [...attributesArray].sort((a: Attribute, b: Attribute) => {
        const aOnline = getAttributeOnlineStatus(a.status);
        const bOnline = getAttributeOnlineStatus(b.status);

        if (aOnline && !bOnline) return -1;
        if (!aOnline && bOnline) return 1;

        return (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });
      });

      return { device, sortedAttributes };
    });
  }, [box]);

  if (!isOpen || !boxId) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="sidebar-overlay fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />

      {/* Sidebar */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:w-[500px] bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/50">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Détails de la Box</h2>
            {box && (
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{box.name}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-icons-outlined" style={{ fontSize: '24px' }}>close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <span className="material-icons-outlined animate-spin text-primary text-4xl mb-4">progress_activity</span>
                <p className="text-slate-500 dark:text-slate-400">Chargement des détails...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <span className="material-icons-outlined text-red-400 text-4xl mb-4">error</span>
                <p className="text-red-400 font-medium mb-2">Erreur de chargement</p>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {error?.response?.status === 403
                    ? "Vous n'avez pas accès à cette box."
                    : error?.response?.status === 404
                    ? 'Box introuvable.'
                    : 'Une erreur est survenue lors du chargement.'}
                </p>
              </div>
            </div>
          ) : !box ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <span className="material-icons-outlined text-slate-400 dark:text-slate-500 text-4xl mb-4">info</span>
                <p className="text-slate-500 dark:text-slate-400">Aucune donnée disponible</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {box.devices && box.devices.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
                    <span className="material-icons-outlined text-primary" style={{ fontSize: '20px' }}>devices</span>
                    Devices ({box.devices.length})
                  </h3>
                  <div className="space-y-4">
                    {devicesWithSortedAttributes.map(({ device, sortedAttributes }) => {
                      return (
                        <div
                          key={device.id}
                          className="bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700 p-4"
                        >
                          {/* Device Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div
                                className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                                  device.isOnline
                                    ? 'bg-green-500/10 border border-green-500/20 text-green-400'
                                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                                }`}
                              >
                                <span className="material-icons-outlined" style={{ fontSize: '16px' }}>
                                  {device.isOnline ? 'check_circle' : 'error_outline'}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">{device.name}</div>
                                <div className="text-xs text-slate-400 dark:text-slate-500">ID: {device.code || device.uuid || `DEV-${device.id}`}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {device.isOnline ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20">
                                  En ligne
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                                  Hors ligne
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Attributes List */}
                          {sortedAttributes.length > 0 ? (
                            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                              <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                                <span className="material-icons-outlined" style={{ fontSize: '14px' }}>tune</span>
                                Attributs ({sortedAttributes.length})
                              </h4>
                              <div className="space-y-1.5">
                                {sortedAttributes.map((attribute: Attribute) => {
                                  const attributeType = typeof attribute.type === 'object' ? attribute.type : null;
                                  const unit = attributeType?.unit || '';
                                  const isOnline = getAttributeOnlineStatus(attribute.status);

                                  return (
                                    <div
                                      key={attribute.id}
                                      className="bg-white dark:bg-slate-800/50 rounded-md px-3 py-2 border border-slate-200 dark:border-slate-700/50 flex items-center justify-between gap-3"
                                    >
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="flex-shrink-0">
                                          <span
                                            className={`inline-block h-2 w-2 rounded-full ${
                                              isOnline
                                                ? 'bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]'
                                                : 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]'
                                            }`}
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-slate-600 dark:text-slate-300 truncate">
                                              {attribute.name}
                                            </span>
                                            {attribute.currentValue !== null && attribute.currentValue !== undefined && (
                                              <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                                {attribute.currentValue}
                                                {unit && <span className="text-slate-400 dark:text-slate-500 ml-1">{unit}</span>}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                              <p className="text-xs text-slate-400 dark:text-slate-500 italic">Aucun attribut disponible</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <span className="material-icons-outlined text-slate-400 dark:text-slate-500 text-4xl mb-4">devices_other</span>
                  <p className="text-slate-500 dark:text-slate-400">Aucun device disponible pour cette box</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
